import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Box, 
  useMediaQuery, 
  useTheme,
  CircularProgress
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { constructionService } from '../../services/construction';
import { IncompleteWork } from '../../types/construction';

interface IncompleteWorksModalProps {
  open: boolean;
  onClose: () => void;
}

const IncompleteWorksModal: React.FC<IncompleteWorksModalProps> = ({ open, onClose }) => {
  const [incompleteWorks, setIncompleteWorks] = useState<IncompleteWork[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<{ [key: number]: boolean }>({});
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      fetchIncompleteWorks();
    }
  }, [open]);

  const fetchIncompleteWorks = async () => {
    setLoading(true);
    setError(null);
    try {
      const works = await constructionService.getIncompleteWorks();
      setIncompleteWorks(works);
    } catch (err) {
      setError('未完了工事リストの取得に失敗しました。');
      console.error('未完了工事リストの取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWork = async (id: number) => {
    // 処理中のIDを記録
    setProcessing(prev => ({ ...prev, [id]: true }));
    
    try {
      await constructionService.completeWork(id);
      
      // 成功したら一覧から削除
      setIncompleteWorks(prev => prev.filter(work => work.ID !== id));
    } catch (err) {
      console.error('工事完了処理エラー:', err);
      setError('工事の完了状態への更新に失敗しました。');
    } finally {
      // 処理完了したIDを処理中から削除
      setProcessing(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy年MM月dd日', { locale: ja });
    } catch (error) {
      return '日付なし';
    }
  };

  // モバイル用の表示
  const renderMobileTable = () => (
    <Box sx={{ width: '100%' }}>
      {incompleteWorks.map((work) => (
        <Paper
          key={work.ID}
          elevation={2}
          sx={{ 
            p: 2, 
            mb: 2, 
            border: '1px solid #e0e0e0',
            borderRadius: 1 
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {work.customerName} - {work.siteName}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {work.constructionDetails}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            最終更新日: {formatDate(work.lastUpdated)}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!!processing[work.ID]}
              onClick={() => handleCompleteWork(work.ID)}
              sx={{ minWidth: '120px' }}
            >
              {processing[work.ID] ? <CircularProgress size={24} color="inherit" /> : '工事完了にする'}
            </Button>
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // PC用の表示
  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>顧客名</TableCell>
            <TableCell>現場名</TableCell>
            <TableCell>工事内容</TableCell>
            <TableCell>最終更新日</TableCell>
            <TableCell align="center">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {incompleteWorks.map((work) => (
            <TableRow key={work.ID}>
              <TableCell>{work.customerName}</TableCell>
              <TableCell>{work.siteName}</TableCell>
              <TableCell>{work.constructionDetails}</TableCell>
              <TableCell>{formatDate(work.lastUpdated)}</TableCell>
              <TableCell align="center">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={!!processing[work.ID]}
                  onClick={() => handleCompleteWork(work.ID)}
                  sx={{ minWidth: '120px' }}
                >
                  {processing[work.ID] ? <CircularProgress size={24} color="inherit" /> : '工事完了にする'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
        未完了工事確認
      </DialogTitle>
      <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : incompleteWorks.length === 0 ? (
          <Typography align="center" variant="body1" sx={{ py: 4 }}>
            未完了の工事はありません
          </Typography>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              以下は現在担当している未完了の工事リストです。完了している工事があれば「工事完了にする」ボタンをクリックしてください。
            </Typography>
            {isMobile ? renderMobileTable() : renderDesktopTable()}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          color="primary"
          fullWidth={isMobile}
        >
          すべて確認しました
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncompleteWorksModal;